/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

import {useSelector} from "react-redux"; // Importing useSelector hook to access Redux store state
import {useNavigate} from "react-router-dom"; // Importing useNavigate hook for routing navigation
import React, {useState} from "react"; // Importing React, useEffect, useState hooks for managing state and lifecycle
import CustomTh from "../../../../components/table/CustomTh"; // Import custom table header component
import CustomTd from "../../../../components/table/CustomTd"; // Import custom table data cell component
import DropDownMenuProfile from "../../../../components/DropDown/DropDownMenuProfile"; // Import dropdown menu for profile actions
import DropDownMenuItem from "../../../../components/DropDown/DropDownMenuItem"; // Import dropdown menu item component
import TableFunction from "../../../../components/table/TableFunction"; // Import table function for handling table logic
import {TABLES} from "../../../../Apis/Tables"; // Import constants for table names
import CustomChip from "../../../../components/CustomChip"; // Import custom chip component for status display
import {status, STATUS_MODEL} from "../../../../enums/statusEnum"; // Import status enum for contractor status values
import {statusBorderColor, statusColor, statusFontColor} from "../../../../enums/statusColorEnum"; // Import status color enum for contractor status color coding
import routes from "../../../../Routes/routes"; // Import routes for navigation
import ContractDataApp from "./ContractDataApp"; // Import contract data management logic for filtering and searching
import {permissionObj} from "../../../../Helpers/permission";
import {TABS_CONTRACTORS} from '../Model/Models';
import {DeleteIconsOptions, DetailsIconsOptions, IconPersonPhoto, UpdateIconsOptions} from '../../../../components/buttons/IconButtons';
import {EMPTY_VALUES, getFullName} from "../../../../Apis/functions"; // Import edit icon for update actions
import { TABS_PROVIDER } from "../../../Provider/Model/Model";

// Main component that displays the contractors' table and handles its logic
const ContractorsTableApp = ({
     selectedList,
     setSelectedList,
     modified,
     setModified,
     setIsAllChecked,
     setDeleteContractorShow,
     searchElement,
     setSearchElement,
     contractorId,
     setCriteria,
     finalArray,
     search, setSearch,
     setBodyForExport
 }) => {

    // Initialize navigate function for routing
    const navigate = useNavigate();

    // Access state values from Redux store
    const DEV = useSelector((state) => state?.CommonsSlice?.development); // Access development state for conditional rendering
    const { permission } = useSelector((state) => state.authenticatioauthennSlice); // Access user permission data

    // States for managing table configurations
    const [tableId, setTableId] = useState("tbl_cta_contractors"); // Set the table ID for identification
    const [searchId, setSearchId] = useState("txt_cta_search_contractors"); // Set the search input ID
    const [results, setResults] = useState(null); // Store the search results
    const [option, setOption] = useState(`${TABLES.CONTRACTORS}`); // Set table option to 'contractors'
    const deletePermission = (DEV || permission.includes(permissionObj?.WEB_CONTRACTOR_DELETE));
    const leftPosition = deletePermission ? 50 : 0;

    // Function to generate dynamic table headers
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} fixedXY={true} left={leftPosition}
                      classes="first_head">{t('company_name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('email')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('phone_number')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('status')}</CustomTh>
        ];
    };

    // Function to generate table body (rows) based on the fetched data
    const getTbody = () => {
        // If no results or no content, return empty body and data
        if (!results || results?.content?.length === 0) return {
            body: [],
            data: []
        };

        // Map through the results and generate a table row for each contractor
        let body = results?.content?.map((item) => {
            return (
                <>
                    <CustomTd tableId={tableId} fixed={true} left={leftPosition} classes={"first-column"}>
                        {item?.acronym} | {' ' + item?.contractorCompanyName}
                    </CustomTd>
                    <CustomTd tableId={tableId}>{item?.fullName || `${getFullName(item?.name, item?.lastName, item?.secondLastName)}`}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.email}</CustomTd>
                    <CustomTd tableId={tableId}>{item?.phoneNumber}</CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip
                            legend={t(status[item?.statusId])}
                            background={statusColor[item?.statusId]}
                            borderColor={statusBorderColor[item.statusId]}
                            fontColor={statusFontColor[item?.statusId]}
                            variant={'large'}
                        />
                    </CustomTd>
                </>
            );
        });

        return {
            body: body, // Return the generated table rows
            data: results?.content // Return the data associated with the rows
        };
    };

    // Function to generate action buttons (approve, update, delete) for each table row
    const getActionsButtons = (item, index) => {
        return <>
            {(DEV || permission.includes(permissionObj?.WEB_CONTRACTOR_UPDATE) || permission.includes(permissionObj?.WEB_CONTRACTOR_DELETE)) ?
                <DropDownMenuProfile
                    openMenu={openMenu}
                    handleMenuToggle={handleMenuToggle}
                    menuId={`menu-${item.id}`} // Unique menu ID for each contractor
                >

                    {/* Update button */}
                    {(DEV || permission.includes(permissionObj?.WEB_CONTRACTOR_UPDATE)) &&
                    <DropDownMenuItem
                        id={"btn_cta_update_"+ item?.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('update')}
                        click={() =>  {
                            navigate(routes.contractor.update+ item.id,
                                { state: {
                                        backUrl: routes.contractor.list,
                                        currentTab: TABS_CONTRACTORS.CONTRACTORS.INDEX,
                                    }
                                }
                            );
                        }
                        } // Navigate to the update page
                        icon={<UpdateIconsOptions/>}
                    />
                    }

                    <DropDownMenuItem
                        id={"btn_cta_details_"+ item?.id}
                        index={index}
                        handleClose={handleClose}
                        name={ t('details')}
                        click={() =>  {
                            navigate(routes.contractor.details +  item?.id,
                                { state: {
                                        backUrl: routes.contractor.list,
                                        currentTab: TABS_CONTRACTORS.CONTRACTORS.INDEX,
                                    }
                                }
                            );
                        }}
                        icon={ <DetailsIconsOptions/> }
                    />
                    <DropDownMenuItem
                            index={index}
                            handleClose={handleClose}
                            name={t('validation_identity')}
                            icon={<IconPersonPhoto />}
                            click={() => {
                              navigate(routes.employee.contractorIdentityValidation +  item?.userId,
                                { state: {
                                        backUrl: routes.contractor.list,
                                        currentTab: TABS_CONTRACTORS.CONTRACTORS.INDEX,
                                    }
                                }
                            );
                            }}
                    />
                    {/* Delete button */}
                    { (DEV || permission.includes(permissionObj?.WEB_CONTRACTOR_DELETE)) &&
                    <DropDownMenuItem
                        id={"btn_cta_delete_" + item?.id}
                        index={index}
                        handleClose={handleClose}
                        name={t('delete')}
                        fontColor='font-color-error-600'
                        iconColor={"icon-color-error-code-600"}
                        click={() => {
                            setDeleteContractorShow(true); // Show delete confirmation modal
                            setSelectedList([item?.id]); // Set selected item ID for deletion
                        }}
                        icon={<DeleteIconsOptions/>}
                    />
                    }
                </DropDownMenuProfile>
                : EMPTY_VALUES.DEFAULT
            }
        </>;
    };

    // Using ContractDataApp to manage search and filter logic
    const { getData, getDataAdvancedSearch } = ContractDataApp({
            searchElement,
            setSearchElement,
            contractorId,
            setCriteria,
            setBodyForExport,
            setResults,
            option,
            modified,
            setModified,
            finalArray,
            search,
            setSearch
        });
    // Using TableFunction component to manage table functionalities like pagination, search, and checkbox
    const { t, thead, tbody, handleCheckboxChange, handleDeleteAll,
        pagination, page, rowsPerPage,
        openMenu, handleMenuToggle, handleClose } = TableFunction({
        selectedList,
        setSelectedList, // Callback to set selected rows
        data: results, // Table data
        getThead, // Function to generate headers
        getTbody, // Function to generate rows
        labelRowsPerPage: 'contractors_per_page', // Label for rows per page
        hasContainer: true, // Whether the table should have a container
        fixedPagination: false, // Whether pagination should be fixed
        setIsAllChecked,

        searchElement,//New
        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray//New - apply only with advanced search
    });

    // Return necessary props and methods to render the table
    return {
        t,
        navigate,
        DEV,
        permission,
        tableId,
        searchId,
        thead,
        tbody,
        option,
        pagination,
        getActionsButtons,
        handleCheckboxChange,
        handleDeleteAll,
        setResults,
        page,
        rowsPerPage,
        deletePermission
    };
}

// Exporting the ContractorsTableApp component to be used elsewhere in the application
export default ContractorsTableApp;
